import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, BarChart3 } from "lucide-react";

const SOURCES = [
  ["all", "Semua Sumber"], ["whatsapp", "WhatsApp"], ["instagram", "Instagram"], ["tiktok", "TikTok"],
  ["ads", "Ads (Meta/Google)"], ["ads_ots", "Ads OTS"], ["ads_cs_cabang", "Ads CS Cabang"], ["walk_in", "Walk-in"], ["referral", "Referral"], ["other", "Lainnya"],
];

export default function PerformanceReports() {
  const { user } = useAuth();
  const isAdmin = ["owner", "super_admin", "manager"].includes(user?.role);
  const [reportType, setReportType] = useState(user?.role === "crm" ? "crm" : "cs");
  const [branches, setBranches] = useState([]);
  const [csUsers, setCsUsers] = useState([]);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", branch_id: "all", source: "all", cs_id: "all" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    if (isAdmin || user?.role === "crm") api.get("/cs-users").then((r) => setCsUsers(r.data)).catch(() => {});
  }, [isAdmin, user]);

  const endpoint = reportType === "crm" ? "/reports/crm-performance" : "/reports/cs-performance";

  const params = useMemo(() => {
    const p = {};
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.branch_id && filters.branch_id !== "all") p.branch_id = filters.branch_id;
    if (filters.source && filters.source !== "all") p.source = filters.source;
    if (reportType === "cs" && isAdmin && filters.cs_id && filters.cs_id !== "all") p.cs_id = filters.cs_id;
    return p;
  }, [filters, reportType, isAdmin]);

  const load = () => {
    setLoading(true);
    api.get(endpoint, { params }).then((r) => setData(r.data)).catch((e) => toast.error("Gagal memuat laporan")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint, params]);

  const doExport = async (fmt) => {
    try {
      const token = localStorage.getItem("myclinic_token");
      const activeClinic = localStorage.getItem("myclinic_active_clinic");
      const headers = { Authorization: `Bearer ${token}` };
      if (activeClinic) headers["X-Clinic-Id"] = activeClinic;
      const qs = new URLSearchParams({ ...params, export: fmt }).toString();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${endpoint}?${qs}`, { headers });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = reportType === "crm" ? `Laporan_CRM.${fmt === "csv" ? "csv" : "xlsx"}` : `Laporan_CS.${fmt === "csv" ? "csv" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Gagal export");
    }
  };

  const cols = data?.columns || [];

  return (
    <div className="space-y-6" data-testid="performance-report-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2 text-emerald-800">
            <BarChart3 className="h-6 w-6 text-emerald-600" />
            {reportType === "crm" ? "Laporan Performa CRM (Tim)" : "Laporan Performa CS"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {reportType === "crm" ? "Agregat reservasi seluruh CS per cabang." : (isAdmin ? "Bandingkan performa CS per cabang & periode." : "Performa Anda per cabang.")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="export-csv-btn" onClick={() => doExport("csv")}><Download className="mr-1.5 h-4 w-4" /> CSV</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-excel-btn" onClick={() => doExport("xlsx")}><Download className="mr-1.5 h-4 w-4" /> Excel</Button>
        </div>
      </div>

      <Card className="border-emerald-100">
        <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-6">
          {(isAdmin) && (
            <div>
              <Label className="text-xs">Jenis Laporan</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger data-testid="report-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cs">Performa CS</SelectItem>
                  <SelectItem value="crm">Performa CRM (Tim)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Dari Tanggal</Label>
            <Input type="date" data-testid="filter-date-from" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Sampai Tanggal</Label>
            <Input type="date" data-testid="filter-date-to" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Cabang</Label>
            <Select value={filters.branch_id} onValueChange={(v) => setFilters({ ...filters, branch_id: v })}>
              <SelectTrigger data-testid="filter-branch-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Cabang</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Sumber Booking</Label>
            <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
              <SelectTrigger data-testid="filter-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {reportType === "cs" && isAdmin && (
            <div>
              <Label className="text-xs">CS</Label>
              <Select value={filters.cs_id} onValueChange={(v) => setFilters({ ...filters, cs_id: v })}>
                <SelectTrigger data-testid="filter-cs-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua CS (gabungan)</SelectItem>
                  {csUsers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {loading && <p className="text-sm text-muted-foreground">Memuat...</p>}

      {data?.sections?.map((sec) => (
        <Card key={sec.key} className="border-emerald-100 overflow-hidden">
          <CardHeader className="bg-emerald-50 py-3">
            <CardTitle className="text-base text-emerald-800">{sec.label}</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-emerald-600 hover:bg-emerald-600">
                  {cols.map((c) => <TableHead key={c.key} className="text-white whitespace-nowrap">{c.label}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sec.rows.map((r, i) => (
                  <TableRow key={i} data-testid={`${sec.key}-row-${i}`}>
                    {cols.map((c) => <TableCell key={c.key} className="whitespace-nowrap text-sm">{r[c.key]}</TableCell>)}
                  </TableRow>
                ))}
                <TableRow className="bg-[#D4AF37]/20 font-bold" data-testid={`${sec.key}-total`}>
                  {cols.map((c) => <TableCell key={c.key} className="whitespace-nowrap text-sm font-bold text-amber-900">{sec.total[c.key]}</TableCell>)}
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
